import { ArrowRight, Bot, Check, ChevronRight, MessageSquareReply, Square, Workflow, X } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { AgentOrb, Button, EmptyState, MeterBar, ProjectMark, Panel, Pressable, Segmented, cn, formatCompact, formatDuration, formatInt, formatMoney, useNow } from '@orgus/ui';
import { useAllSessions, useOrgus } from '../../lib/store.ts';
import { openNewSession } from '../new-session/store.ts';
import { useSessionParam } from '../sessions/useSessionParam.ts';
import { useSessionCost } from '../costs/useCosts.ts';
import { liveCards, liveGroups } from './live.ts';
import { AgentGlyph, agentHue } from '../adapters/AgentKind.tsx';
import { useNeedsYouState } from '../asks/needsYou.ts';
import type { LiveCard, LiveChild } from './live.ts';
import type { AgentOrbState } from '@orgus/ui';

type Show = 'all' | 'needs-you' | 'asked' | 'working';

/**
 * The list holds this many before it asks you to open Sessions; the panel scrolls inside and
 * shows about nine. 200 rows were 4,400 elements of the Overview's 11,000 at scale, laid out on
 * every open (21 Oct 2026).
 */
const CAP = 50;

/** The three numbers at a row's end: fixed widths, so they line up down the list and under their labels. */
const NUM = { tokens: 'w-14', cost: 'w-14', running: 'w-16' } as const;

function Cost({ id }: { id: string }) {
  const u = useSessionCost(id);
  return <>{u && u.total.cost > 0 ? formatMoney(u.total.cost) : ''}</>;
}

/** The mark stays the agent's own (working or at rest); the outcome is said once, in the Running column. */
const CHILD_ORB: Record<LiveChild['status'], AgentOrbState> = { running: 'working', done: 'ended', failed: 'ended', stopped: 'ended' };

/** An ended child's outcome, as a row status: icon plus label, grey unless it failed. */
const OUTCOME = {
  done: { icon: Check, label: 'Done', cls: 'text-fg-subtle' },
  failed: { icon: X, label: 'Failed', cls: 'text-status-danger' },
  stopped: { icon: Square, label: 'Stopped', cls: 'text-fg-subtle' },
} as const;

/** A subagent or workflow under its session: its own mark, what it does (a workflow's progress beside it), and how long it ran or how it ended. */
function ChildRow({ k, hue, now, open }: { k: LiveChild; hue: string; now: number; open: () => void }) {
  const ran = (k.endedAt ?? now) - k.startedAt;
  const end = k.status === 'running' ? null : OUTCOME[k.status];
  return (
    <li>
      <Pressable onClick={open} className="flex min-h-row w-full min-w-0 items-center gap-3 rounded-control px-3 py-1 text-left transition-colors duration-(--duration-fast) hover:bg-hover">
        <AgentOrb state={CHILD_ORB[k.status]} size={16} hue={hue} icon={k.kind === 'workflow' ? <Workflow /> : <Bot />} label={`${k.kind === 'workflow' ? 'Workflow' : 'Subagent'}, ${end ? end.label.toLowerCase() : 'running'}`} />
        <span className="flex min-w-0 flex-1 items-center gap-2">
          {/* On a phone the label takes two lines before it is cut. */}
          <span className={cn('min-w-0 truncate text-caption max-sm:line-clamp-2 max-sm:whitespace-normal', k.status === 'running' ? 'text-fg-muted' : 'text-fg-subtle')} title={k.label}>
            {k.label}
          </span>
          {k.progress && (
            <span className="flex shrink-0 items-center gap-2 text-caption text-fg-subtle num">
              <MeterBar value={k.progress.total ? k.progress.done / k.progress.total : 0} tone="working" label={`${k.label}: ${formatInt(k.progress.done)} of ${formatInt(k.progress.total)} agents done`} className="w-12 max-sm:hidden" />
              {formatInt(k.progress.done)} of {formatInt(k.progress.total)}
            </span>
          )}
        </span>
        <span className={cn(NUM.running, 'flex shrink-0 items-center justify-end gap-1 whitespace-nowrap text-caption num', end ? end.cls : 'text-fg-subtle')}>
          {end ? (
            <>
              <end.icon className="size-3" aria-hidden />
              {end.label}
            </>
          ) : (
            formatDuration(Math.max(0, ran))
          )}
        </span>
      </Pressable>
    </li>
  );
}

function Row({ c, machine, now, open, expanded, onToggle }: { c: LiveCard; machine: string; now: number; open: () => void; expanded: boolean; onToggle: () => void }) {
  const asks = c.state === 'needs-you';
  const asked = c.state === 'asked';
  const waiting = asks || asked;
  const running = c.children.filter((k) => k.status === 'running').length;
  const hue = agentHue(c.agentKind);
  return (
    <li className="relative flex flex-col" data-row>
      {/* Its subagents and workflows fold under it: the chevron sits in the indent, left of the mark. */}
      {c.children.length > 0 && (
        <Pressable
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Hide' : 'Show'} the subagents and workflows of ${c.title}`}
          className="absolute -left-6 top-0 flex h-row-2line w-6 items-center justify-center rounded-control text-fg-subtle transition-colors duration-(--duration-fast) hover:text-fg"
        >
          <ChevronRight className={cn('size-3 transition-transform duration-(--duration-fast)', expanded && 'rotate-90')} aria-hidden />
        </Pressable>
      )}
      <div className="relative">
        <Pressable
          onClick={open}
          className="flex min-h-row-2line w-full min-w-0 items-center gap-3 rounded-control px-3 py-1 text-left transition-colors duration-(--duration-fast) hover:bg-hover"
        >
          <AgentOrb state={c.state} size={20} hue={hue} icon={<AgentGlyph kind={c.agentKind} />} label={asks ? 'Needs you' : asked ? 'Asked' : 'Working'} />
          {/* The title, and under it, small and dim, what it does or asks this moment. */}
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="flex min-w-0 items-baseline gap-2">
              {/* On a phone the title is the row: it takes two lines, and the agent count gives way. */}
              <span className="truncate text-ui font-medium text-fg max-sm:line-clamp-2 max-sm:whitespace-normal" title={`${c.title} on ${machine}`}>
                {c.title}
              </span>
              {running > 0 && <span className="shrink-0 text-caption text-fg-subtle num max-sm:hidden">{running === 1 ? '1 agent' : `${formatInt(running)} agents`}</span>}
            </span>
            <span key={c.now} className="motion-arrive truncate text-caption text-fg-subtle" title={c.now}>
              {waiting && <span className="mr-1 text-fg-muted">Asks</span>}
              {c.now}
            </span>
          </span>
          <span className={cn('flex shrink-0 items-center text-caption text-fg-subtle num', waiting && 'invisible')}>
            <span className={cn(NUM.tokens, 'hidden text-right sm:block')}>{c.tokens > 0 ? `${formatCompact(c.tokens)}${c.partial ? '+' : ''}` : ''}</span>
            <span className={cn(NUM.cost, 'hidden text-right sm:block')}>
              <Cost id={c.id} />
            </span>
            <span className={cn(NUM.running, 'text-right')}>{formatDuration(Math.max(0, now - c.startedAt))}</span>
          </span>
        </Pressable>
        {waiting && (
          <Button size="sm" variant="secondary" icon={<MessageSquareReply />} className="absolute right-3 top-1/2 -translate-y-1/2" onClick={open}>
            Answer
          </Button>
        )}
      </div>
      {expanded && (
        <ul className="motion-arrive flex flex-col pb-1 pl-8 max-sm:pl-4" aria-label={`Subagents and workflows of ${c.title}`}>
          {c.children.map((k) => (
            <ChildRow key={k.id} k={k} hue={hue} now={now} open={open} />
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * Every agent at work or waiting, at any scale, by project: what needs you on top, then work, the
 * newest activity first. A row is its title with what it does or asks this moment under it, and
 * its tokens, cost and running time in three quiet columns; each project opens under a slim
 * labelled line in its own hue.
 */
export function LiveNow({ className }: { className?: string }) {
  const sessions = useAllSessions();
  const projects = useOrgus((s) => s.projects);
  const machines = useOrgus((s) => s.machines);
  const navigate = useNavigate();
  const [, openSession] = useSessionParam();
  const now = useNow(10_000);
  const [show, setShow] = useState<Show>('all');
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const toggle = (id: string) =>
    setExpanded((s) => {
      const n = new Set(s);
      if (!n.delete(id)) n.add(id);
      return n;
    });
  // The one selector (ADR 0056): asked questions list after what blocks, quietly; old or dismissed ones leave.
  const levels = useNeedsYouState().bySession;
  const cards = useMemo(() => liveCards(sessions, levels), [sessions, levels]);
  // Needs you is what blocks, as on the Live sessions tile and the Sessions page; an asked
  // question (nothing held) is its own quiet state, Asked, as in the session drawer. The parts
  // add up to All, and a part with nothing in it is not offered.
  const count: Record<Exclude<Show, 'all'>, number> = { 'needs-you': 0, asked: 0, working: 0 };
  for (const c of cards) count[c.state]++;
  const view: Show = show !== 'all' && count[show] === 0 ? 'all' : show;
  const shown = cards.filter((c) => view === 'all' || c.state === view);
  const groups = useMemo(() => liveGroups(shown.slice(0, CAP)), [shown]);
  // The three column labels only over rows that show their numbers (a waiting row shows Answer).
  const numbers = shown.some((c) => c.state === 'working');
  const filter =
    cards.length > 0 ? (
      <Segmented<Show>
        label="Show"
        value={view}
        onChange={setShow}
        options={[
          { value: 'all' as const, label: 'All', count: cards.length },
          { value: 'needs-you' as const, label: 'Needs you', count: count['needs-you'] },
          { value: 'asked' as const, label: 'Asked', count: count.asked },
          { value: 'working' as const, label: 'Working', count: count.working },
        ].filter((o) => o.value === 'all' || o.count > 0)}
      />
    ) : null;

  return (
    <Panel
      icon={<Bot />}
     
      title="Live now"
      sub={formatInt(cards.length)}
      className={className}
      flush
      // On a phone the filter takes its own line under the title, so the title keeps its place.
      actions={filter ? <span className="max-sm:hidden">{filter}</span> : undefined}
    >
      {filter && <div className="flex px-4 pb-2 sm:hidden">{filter}</div>}
      {cards.length === 0 ? (
        <EmptyState icon={<Bot />} title="No agent is working" description="Agents at work show here, with what each is doing now." action={<Button onClick={() => openNewSession()}>New session</Button>} />
      ) : (
        <>
          <div className="flex max-h-128 flex-col overflow-y-auto px-1 pb-1">
            {groups.map((g, i) => {
              const name = g.projectId ? (projects[g.projectId]?.name ?? 'Unknown project') : 'No project';
              return (
                <section key={g.projectId ?? 'none'} aria-label={name} className="flex flex-col">
                  {/* A slim labelled line opens each project: its mark, its name and count, then a hairline. */}
                  <h3 className="flex h-8 items-center gap-2 px-3 text-caption">
                    <ProjectMark projectId={g.projectId} name={name} size="xs" />
                    <span className="shrink-0 font-medium text-fg-muted">{name}</span>
                    <span className="shrink-0 text-fg-subtle num">{formatInt(g.cards.length)}</span>
                    <span aria-hidden className="h-px min-w-4 flex-1 bg-border-subtle" />
                    {/* The first line also names the three columns, over them: no header row of its own. */}
                    {i === 0 && numbers && (
                      <span aria-hidden className="flex shrink-0 items-center text-fg-subtle">
                        <span className={cn(NUM.tokens, 'hidden text-right sm:block')}>Tokens</span>
                        <span className={cn(NUM.cost, 'hidden text-right sm:block')}>Cost</span>
                        <span className={cn(NUM.running, 'text-right')}>Running</span>
                      </span>
                    )}
                  </h3>
                  <ul className="flex flex-col pl-6" aria-label={`Agents at work in ${name}`}>
                    {g.cards.map((c) => (
                      <Row key={c.id} c={c} machine={machines[c.machineId]?.name ?? 'Unknown'} now={now} open={() => openSession(c.id)} expanded={expanded.has(c.id)} onToggle={() => toggle(c.id)} />
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
          {shown.length > CAP && (
            <div className="flex justify-center pb-3">
              <Button variant="ghost" size="sm" iconRight={<ArrowRight />} onClick={() => navigate('/sessions')}>
                {formatInt(shown.length - CAP)} more in Sessions
              </Button>
            </div>
          )}
        </>
      )}
    </Panel>
  );
}
